import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Paragraph } from 'components/Base/Typography';
import { Lightning } from 'assets/icon/icon';
import { Tabs, Collapse, Panel } from '@idaho-aeyc/ui-kit';
import SubHeader from 'components/Library/Header/HeaderExports';
import { EIconNames, Icon } from 'components/Base/Icon';
import { Row, Col } from 'components/Base/Grid';
import { useGetPillarsOfStrengthQuery } from 'redux/apiSlice/pillars.slice';
import { useAddTacticToActionPlanMutation } from 'redux/apiSlice/pillarsActionPlan.slice';
import Spinner from 'components/Base/Spinner';
import { Alert } from 'components/Base/Alert';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import Banner from 'pages/private/ELCs/PillarsOfStrength/Components/Banner/BannerExports';
import Card from 'pages/private/ELCs/PillarsOfStrength/Components/Card/CardExports';
import {
  EPillarsOfStrengthTabs,
  TPillarsOfStrengthProps,
} from './PillarsOfStrength.types';

const GlobalStyle = createGlobalStyle`
  .ant-collapse.ant-collapse-icon-position-start {
    background-color: #f5f5f5 !important;
  }
  .ant-collapse .ant-collapse-item {
    overflow: hidden;
  }
  .ant-collapse .ant-collapse-item .ant-collapse-header {
    background-color: white !important;
  }
  .ant-collapse .ant-collapse-item .ant-collapse-content-box {
    padding: 16px !important;
  }
`;

interface Tactic {
  id: string;
  title: string;
  description: string;
  tacticId: number;
  actionPlanId?: number;
  status?: string;
}

interface Level {
  id: string;
  name: string;
  description: string;
  tactics: Tactic[];
}

interface SubPillar {
  id: string;
  description: string;
  levels: Level[];
}

interface Pillar {
  id: string;
  name: string;
  description: string;
  subPillars: SubPillar[];
}

const PageContainer = styled.div`
  background-color: #f5f5f5;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #fff;
  box-shadow: none;
`;

const StyledTabs = styled(Tabs)`
  margin-top: 8px;
  box-shadow: none;

  .ant-tabs-nav {
    margin-bottom: 0;
    border-bottom: none;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background-color: transparent;
    }
  }

  .ant-tabs-content-holder {
    margin: 0;
    padding: 16px;
    background-color: #f5f5f5;
    border: none;
    border-top: none;
    max-height: calc(100vh - 200px);
    overflow-y: auto;

    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background: #bdbdbd;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #a6a6a6;
    }

    @media (max-width: 1200px) {
      max-height: calc(100vh - 180px);
    }

    @media (max-width: 768px) {
      max-height: calc(100vh - 150px);
    }

    @media (max-width: 480px) {
      max-height: calc(100vh - 120px);
    }
  }

  .ant-tabs-content {
    margin: 0;
    padding: 0;
    min-height: 0;
    border: none;
  }
`;

const LevelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 24px;
  padding: 0;

  @media (max-width: 1200px) {
    gap: 20px;
  }

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const StyledCard = styled(Card)`
  width: calc(33.33% - 16px);
  margin-bottom: 0;
  background: white;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 8px;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 1200px) {
    width: calc(50% - 12px);
  }

  @media (max-width: 992px) {
    width: 100%;
  }
`;

const StyledBanner = styled(Banner)`
  margin-bottom: 0;
  box-shadow: none;
`;

const GoalsTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 16px 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    border-radius: 3px;
  }
`;

const TacticsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TacticsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 0;

  @media (max-width: 1200px) {
    gap: 20px;
  }

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const TacticCard = styled(StyledCard)`
  width: calc(33.33% - 16px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 200px;

  @media (max-width: 1200px) {
    width: calc(50% - 12px);
  }

  @media (max-width: 992px) {
    width: 100%;
  }
`;

const SimpleHeaderContainer = styled.div`
  display: flex;
  padding: 16px 0px;
  background-color: #f5f5f5;
  overflow: hidden;
`;

const BackButtonRow = styled(Row)`
  cursor: pointer;
  width: 100%;
  overflow: hidden;
`;

const HeaderTitle = styled.h2`
  font-size: 16px;
  margin: 0;
  font-weight: 600;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100vw - 100px);
`;

interface SimpleHeaderProps {
  title: string;
  onBackClick: () => void;
  backLabel?: string;
}

const SimpleHeader = ({ title, onBackClick, backLabel }: SimpleHeaderProps) => {
  return (
    <SimpleHeaderContainer>
      <BackButtonRow align="middle" gutter={[8, 0]} onClick={onBackClick}>
        <Col flex="24px">
          <Icon
            icon={EIconNames.ARROW_BACK}
            size={24}
            color="#000000"
            alt="back icon"
          />
        </Col>
        <Col flex="auto">
          <HeaderTitle title={backLabel || title}>
            {backLabel || title}
          </HeaderTitle>
        </Col>
      </BackButtonRow>
    </SimpleHeaderContainer>
  );
};

interface TabItem {
  key: string;
  label: string;
  children: ReactNode;
  disabled?: boolean;
}

interface PageHeaderWithContentProps {
  subtitle?: string;
  subtitleIcon?: ReactNode;
  tabs?: TabItem[];
  activeTabKey?: string;
  onTabChange?: (key: string) => void;
}

const PageHeaderWithContent = ({
  subtitle,
  subtitleIcon,
  tabs,
  activeTabKey,
  onTabChange,
}: PageHeaderWithContentProps) => {
  return (
    <HeaderContainer>
      {subtitle && (
        <SubHeader title={subtitle} icon={subtitleIcon} iconSize={16} />
      )}

      {tabs && tabs.length > 0 && (
        <StyledTabs
          activeKey={activeTabKey}
          items={tabs}
          onChange={onTabChange}
          destroyInactiveTabPane={false}
        />
      )}
    </HeaderContainer>
  );
};

const PillarContent = ({
  pillar,
  onSelectedLevelChange,
  onTacticAction,
  getTacticButtonConfig,
  forceUpdateKey,
  updatedTactics,
  isPillarsManagementEnabled,
}: {
  pillar: Pillar | null;
  onSelectedLevelChange: (
    level: Level | null,
    subPillar: SubPillar | null,
    tactic?: Tactic | null,
  ) => void;
  onTacticAction?: (tactic: Tactic, action: string) => void;
  getTacticButtonConfig?: (tactic: Tactic) => {
    text: string;
    variant: 'primary' | 'default';
    disabled: boolean;
    action: string;
  };
  forceUpdateKey?: number;
  updatedTactics?: {
    [key: string]: { actionPlanId: number; status: string; tacticId?: number };
  };
  isPillarsManagementEnabled?: boolean;
}) => {
  const [selectedSubPillar, setSelectedSubPillar] = useState<SubPillar | null>(
    null,
  );
  const [showTactics, setShowTactics] = useState(false);
  const [activeTacticKey, setActiveTacticKey] = useState<string | string[]>('');
  const [activeSubPillarKey, setActiveSubPillarKey] = useState<
    string | string[]
  >('');

  if (!pillar) {
    return <Paragraph>No data available for this pillar.</Paragraph>;
  }

  const handleBackClick = () => {
    onSelectedLevelChange(null, null, null);
    setShowTactics(false);
    setSelectedSubPillar(null);
    setActiveTacticKey('');
  };

  const handleTacticKeyChange = (key: string | string[]) => {
    setActiveTacticKey(key);

    if (key && typeof key === 'string' && selectedSubPillar) {
      const levelId = key.replace('level-', '');
      const level = selectedSubPillar.levels.find(lvl => lvl.id === levelId);
      if (level) {
        onSelectedLevelChange(level, selectedSubPillar, null);
      } else {
        console.warn('Could not find level with ID:', levelId);
      }
    } else if (!key || (Array.isArray(key) && key.length === 0)) {
      onSelectedLevelChange(null, selectedSubPillar, null);
    }
  };

  const handleTacticSelection = (tactic: Tactic, level: Level) => {
    onSelectedLevelChange(level, selectedSubPillar, tactic);
  };

  const handleSubPillarPanelChange = (key: string | string[]) => {
    setActiveSubPillarKey(key);
    if (key && typeof key === 'string') {
      const subPillar = pillar.subPillars.find(sp => sp.id === key);
      if (subPillar) {
        setSelectedSubPillar(subPillar);
        onSelectedLevelChange(null, subPillar, null);
      } else {
        console.warn('Could not find subpillar with ID:', key);
      }
    } else if (!key || (Array.isArray(key) && key.length === 0)) {
      setSelectedSubPillar(null);
      onSelectedLevelChange(null, null, null);
    }
  };

  const handleSubPillarClick = (subPillar: SubPillar) => {
    // Set selected subPillar for breadcrumbs
    setSelectedSubPillar(subPillar);
    onSelectedLevelChange(null, subPillar, null);

    // Update active key to ensure panel stays open
    const subPillarPanelKey = subPillar.id;
    setActiveSubPillarKey(subPillarPanelKey);
  };

  const handleLevelPanelClick = (level: Level) => {
    // Make sure level is selected for breadcrumbs
    onSelectedLevelChange(level, selectedSubPillar, null);

    // Update active key to ensure panel stays open
    const levelPanelKey = `level-${level.id}`;
    setActiveTacticKey(levelPanelKey);
  };

  const handleLevelCardClick = (level: Level, subPillar: SubPillar) => {
    onSelectedLevelChange(level, subPillar, null);

    setSelectedSubPillar(subPillar);
    setShowTactics(true);

    const levelPanelKey = `level-${level.id}`;
    setActiveTacticKey(levelPanelKey);
  };

  if (showTactics && selectedSubPillar) {
    return (
      <div>
        <StyledBanner
          title={pillar.name}
          subtitle={pillar.description}
          number={pillar.id}
        />
        <SimpleHeader
          title={`${selectedSubPillar?.id || ''}: ${
            selectedSubPillar?.description || ''
          }`}
          onBackClick={handleBackClick}
          backLabel={selectedSubPillar.description || 'Back'}
        />
        <TacticsList>
          <Collapse
            accordion
            activeKey={activeTacticKey}
            onChange={handleTacticKeyChange}
            expandIconPosition="start"
            ghost={false}
          >
            {selectedSubPillar.levels.map((level: Level) => {
              const panelKey = `level-${level.id}`;
              return (
                <Panel
                  key={panelKey}
                  header={
                    <div
                      onClick={() => {
                        handleLevelPanelClick(level);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleLevelPanelClick(level);
                          e.preventDefault();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                    >
                      {level.name.charAt(0).toUpperCase() +
                        level.name.slice(1).toLowerCase()}
                    </div>
                  }
                >
                  <TacticsContainer>
                    {level.tactics.map(tactic => {
                      const buttonConfig = getTacticButtonConfig?.(tactic) || {
                        text: 'Add to Action Plan',
                        variant: 'primary' as const,
                        disabled: false,
                        action: 'add',
                      };

                      return (
                        <TacticCard
                          key={`${tactic.id}-${
                            updatedTactics?.[tactic.id]?.actionPlanId ||
                            tactic.actionPlanId ||
                            'none'
                          }-${
                            updatedTactics?.[tactic.id]?.status ||
                            tactic.status ||
                            'none'
                          }-${forceUpdateKey || 0}`}
                          title={tactic.title}
                          description={tactic.description}
                          tactics={[]}
                          showTacticCount={false}
                          showButton={!!isPillarsManagementEnabled}
                          buttonText={buttonConfig.text}
                          buttonVariant={buttonConfig.variant}
                          buttonDisabled={buttonConfig.disabled}
                          onButtonClick={() =>
                            onTacticAction?.(tactic, buttonConfig.action)
                          }
                          titleMaxLength={25}
                          characterLimit={100}
                          onClick={() => handleTacticSelection(tactic, level)}
                          data-cy={`tactic${tactic.id.replace(
                            /\D/g,
                            '',
                          )}_description`}
                          showMoreDataCy={`tactic${tactic.id.replace(
                            /\D/g,
                            '',
                          )}_show_more`}
                        />
                      );
                    })}
                  </TacticsContainer>
                </Panel>
              );
            })}
          </Collapse>
        </TacticsList>
      </div>
    );
  }

  return (
    <div>
      <StyledBanner
        title={pillar.name}
        subtitle={pillar.description}
        number={pillar.id}
      />

      <GoalsTitle>Goals</GoalsTitle>

      <div>
        <Collapse
          defaultActiveKey={[]}
          accordion
          activeKey={activeSubPillarKey}
          onChange={handleSubPillarPanelChange}
          expandIconPosition="start"
          ghost={false}
        >
          {pillar.subPillars.map((subPillar: SubPillar) => {
            const subPillarPanelKey = subPillar.id;
            return (
              <Panel
                key={subPillarPanelKey}
                data-cy={`goal_${subPillar.id.replace(/\D/g, '')}`}
                header={
                  <div
                    onClick={() => {
                      handleSubPillarClick(subPillar);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSubPillarClick(subPillar);
                        e.preventDefault();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                  >
                    {`${subPillar.id}: ${subPillar.description}`}
                  </div>
                }
              >
                <LevelsContainer>
                  {subPillar.levels.map((level: Level) => (
                    <StyledCard
                      key={level.id}
                      title={level.name}
                      description={level.description}
                      buttonText="See Tactics"
                      onButtonClick={() =>
                        handleLevelCardClick(level, subPillar)
                      }
                      tactics={level.tactics}
                      data-cy={`level${level.id.replace(
                        /\D/g,
                        '',
                      )}_description`}
                    />
                  ))}
                </LevelsContainer>
              </Panel>
            );
          })}
        </Collapse>
      </div>
    </div>
  );
};

const PillarsOfStrength = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  elcName,
  onLevelSelected,
}: TPillarsOfStrengthProps) => {
  const params = useParams();
  const { id } = params;
  const navigate = useNavigate();
  const location = useLocation();
  const { showToastNotification } = useShowToastNotification();
  const isPillarsManagementEnabled = useIsFeatureEnabled('pillars_management');
  const isFromMyELC = location.pathname.includes('/my-elc/');

  const [activeTab, setActiveTab] = useState<EPillarsOfStrengthTabs>(
    EPillarsOfStrengthTabs.COMMUNITIES,
  );
  const [activePillar, setActivePillar] = useState<Pillar | null>(null);
  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);
  const [updatedTactics, setUpdatedTactics] = useState<{
    [key: string]: { actionPlanId: number; status: string; tacticId?: number };
  }>({});
  const isOptimisticUpdate = useRef(false);
  const optimisticPillarData = useRef<Pillar | null>(null);

  // Fetch pillars of strength data from API
  const {
    data: pillarsData,
    isLoading,
    error,
    refetch,
  } = useGetPillarsOfStrengthQuery({ elcId: id || '' }, { skip: !id });

  // Add tactic to action plan mutation
  const [addTacticToActionPlan] = useAddTacticToActionPlanMutation();

  // Clear optimistic state when component mounts (when navigating back from action plan)
  useEffect(() => {
    setUpdatedTactics({});
    isOptimisticUpdate.current = false;
    optimisticPillarData.current = null;
    setForceUpdateCounter(0);

    if (id) {
      refetch();
    }
  }, [id, refetch]);

  // Cleanup optimistic state when component unmounts (when navigating away)
  useEffect(() => {
    return () => {
      setUpdatedTactics({});
      isOptimisticUpdate.current = false;
      optimisticPillarData.current = null;
    };
  }, []);

  const getPillarByTabKey = useCallback(
    (tabKey: string): Pillar | undefined => {
      const tabToPillarMap: Record<string, string> = {
        [EPillarsOfStrengthTabs.COMMUNITIES]: 'Communities',
        [EPillarsOfStrengthTabs.SERVICES]: 'Services',
        [EPillarsOfStrengthTabs.NEIGHBORHOODS]: 'Neighborhoods',
        [EPillarsOfStrengthTabs.POLICY]: 'Policies',
      };

      const pillarName = tabToPillarMap[tabKey];
      return pillarsData?.pillars.find(pillar => pillar.name === pillarName) as
        | Pillar
        | undefined;
    },
    [pillarsData],
  );

  const getTacticButtonConfig = (tactic: Tactic) => {
    const tacticUpdate = updatedTactics[tactic.id];
    const actionPlanId = tacticUpdate?.actionPlanId || tactic.actionPlanId;
    const status = tacticUpdate?.status || tactic.status;

    if (!actionPlanId) {
      return {
        text: 'Add to Action Plan',
        variant: 'primary' as const,
        disabled: false,
        action: 'add',
      };
    }

    if (status?.toLowerCase() === 'completed') {
      return {
        text: 'Repeat',
        variant: 'default' as const,
        disabled: false,
        action: 'repeat',
      };
    }

    return {
      text: 'See on Action Plan',
      variant: 'default' as const,
      disabled: false,
      action: 'view',
    };
  };

  // Handle tactic button clicks
  const handleTacticAction = async (tactic: Tactic, action: string) => {
    if (!id) {
      console.error('No ELC ID available');
      return;
    }

    try {
      if (action === 'add' || action === 'repeat') {
        // Add tactic to action plan
        const response = await addTacticToActionPlan({
          elcId: id,
          tacticId: tactic.id,
        }).unwrap();

        // Show success notification
        showToastNotification({
          type: ENotificationType.SUCCESS,
          message: 'The Tactic was added to board',
        });

        setUpdatedTactics(prev => ({
          ...prev,
          [tactic.id]: {
            actionPlanId: response.pillarId,
            status: 'IN_PROGRESS',
            tacticId: Number(tactic.id),
          },
        }));
        setForceUpdateCounter(prev => prev + 1);
        if (activePillar) {
          const updatedPillar = {
            ...activePillar,
            subPillars: activePillar.subPillars.map(subPillar => ({
              ...subPillar,
              levels: subPillar.levels.map(level => ({
                ...level,
                tactics: level.tactics.map(t =>
                  t.id === tactic.id
                    ? {
                        ...t,
                        actionPlanId: response.pillarId,
                        status: 'IN_PROGRESS',
                      }
                    : t,
                ),
              })),
            })),
          };

          optimisticPillarData.current = updatedPillar;
          isOptimisticUpdate.current = true;
          setActivePillar(updatedPillar);
        }
      } else if (action === 'view') {
        const tacticId =
          updatedTactics[tactic.id]?.tacticId || tactic.tacticId || tactic.id;

        if (tacticId) {
          const basePath = isFromMyELC ? 'my-elc' : 'elc';
          navigate(`/app/${basePath}/${id}/action-plan?tacticId=${tacticId}`);
        } else {
          console.error('No tacticId available for tactic:', tactic.id);
          showToastNotification({
            type: ENotificationType.ERROR,
            message: `Unable to navigate to action plan. Missing tacticId for tactic ${tactic.id}. Please contact support.`,
          });
        }
      }
    } catch (err: any) {
      console.error('Error handling tactic action:', err);
      showToastNotification({
        type: ENotificationType.ERROR,
        message:
          err?.data?.message || 'Failed to perform action. Please try again.',
      });
    }
  };

  useEffect(() => {
    if (pillarsData) {
      const pillar = getPillarByTabKey(activeTab);
      if (pillar) {
        if (
          isOptimisticUpdate.current &&
          optimisticPillarData.current?.name === pillar.name
        ) {
          setActivePillar(optimisticPillarData.current);
        } else if (
          !isOptimisticUpdate.current ||
          activePillar?.name !== pillar.name
        ) {
          setActivePillar(pillar);
          isOptimisticUpdate.current = false;
          optimisticPillarData.current = null;
        }
      } else {
        setActivePillar(null);
        isOptimisticUpdate.current = false;
        optimisticPillarData.current = null;
      }
    }
  }, [activeTab, id, pillarsData, getPillarByTabKey, activePillar?.name]);

  const handleTabChange = (tab: string) => {
    isOptimisticUpdate.current = false;
    optimisticPillarData.current = null;
    setActiveTab(tab as EPillarsOfStrengthTabs);
  };

  const handleSelectedLevelChange = (
    level: Level | null,
    subPillar: SubPillar | null,
    tactic?: Tactic | null,
  ) => {
    if (onLevelSelected) {
      if (subPillar || level) {
        onLevelSelected(level, subPillar, tactic);
      } else if (!subPillar && !level) {
        onLevelSelected(null, null, null);
      }
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <PageContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <Spinner />
        </div>
      </PageContainer>
    );
  }

  // Show error state
  if (error) {
    return (
      <PageContainer>
        <div style={{ padding: '20px' }}>
          <Alert
            message="Error loading Pillars of Strength"
            description="Unable to load the pillars data. Please try again later."
            type="error"
            showIcon
          />
        </div>
      </PageContainer>
    );
  }

  // Show empty state if no data
  if (!pillarsData || !pillarsData.pillars.length) {
    return (
      <PageContainer>
        <div style={{ padding: '20px' }}>
          <Alert
            message="No Pillars Data Available"
            description="No pillars of strength data is available for this ELC."
            type="info"
            showIcon
          />
        </div>
      </PageContainer>
    );
  }

  const pillarsOfStrengthTabs = [
    {
      key: EPillarsOfStrengthTabs.COMMUNITIES,
      label: 'Communities',
      'data-cy': 'communities_tab',
      children: (
        <PillarContent
          pillar={activePillar}
          onSelectedLevelChange={handleSelectedLevelChange}
          onTacticAction={handleTacticAction}
          getTacticButtonConfig={getTacticButtonConfig}
          forceUpdateKey={forceUpdateCounter}
          updatedTactics={updatedTactics}
          isPillarsManagementEnabled={isPillarsManagementEnabled}
        />
      ),
    },
    {
      key: EPillarsOfStrengthTabs.SERVICES,
      label: 'Services',
      'data-cy': 'services_tab',
      children: (
        <PillarContent
          pillar={activePillar}
          onSelectedLevelChange={handleSelectedLevelChange}
          onTacticAction={handleTacticAction}
          getTacticButtonConfig={getTacticButtonConfig}
          forceUpdateKey={forceUpdateCounter}
          updatedTactics={updatedTactics}
          isPillarsManagementEnabled={isPillarsManagementEnabled}
        />
      ),
    },
    {
      key: EPillarsOfStrengthTabs.NEIGHBORHOODS,
      label: 'Neighborhoods',
      'data-cy': 'neighborhoods_tab',
      children: (
        <PillarContent
          pillar={activePillar}
          onSelectedLevelChange={handleSelectedLevelChange}
          onTacticAction={handleTacticAction}
          getTacticButtonConfig={getTacticButtonConfig}
          forceUpdateKey={forceUpdateCounter}
          updatedTactics={updatedTactics}
          isPillarsManagementEnabled={isPillarsManagementEnabled}
        />
      ),
    },
    {
      key: EPillarsOfStrengthTabs.POLICY,
      label: 'Policy',
      'data-cy': 'policy_tab',
      children: (
        <PillarContent
          pillar={activePillar}
          onSelectedLevelChange={handleSelectedLevelChange}
          onTacticAction={handleTacticAction}
          getTacticButtonConfig={getTacticButtonConfig}
          forceUpdateKey={forceUpdateCounter}
          updatedTactics={updatedTactics}
          isPillarsManagementEnabled={isPillarsManagementEnabled}
        />
      ),
    },
  ];

  return (
    <PageContainer>
      <GlobalStyle />
      <PageHeaderWithContent
        subtitle="Pillars of Strength"
        subtitleIcon={<Lightning />}
        tabs={pillarsOfStrengthTabs}
        activeTabKey={activeTab}
        onTabChange={handleTabChange}
      />
    </PageContainer>
  );
};

export default PillarsOfStrength;
